import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import first from 'lodash/first';
import type { CreditCardAccessType } from '@spotnana/types/openapi/models/credit-card-access-type';
import TravelerDetailsResponseManager from '../services/TravelerDetailsResponseManager';
import type {
  ITraveler,
  ITravelerPersonalInfo,
  IUserOrgId,
  IAirPref,
  ITravelerName,
  IPhoneNumber,
  IPostalAddress,
  IPassport,
  IFlightPnrSummary,
  IEmailConfiguration,
  ITravelerPaymentInfo,
  ITravelerLoyaltyInfos,
  ITravelerLoyaltyInfoV2,
} from '../types/traveler';
import { IUpdateTravelerRequestTypeEnum } from '../types/traveler';
import type { Traveler } from '../types/api/v1/obt/common/traveler';
import {
  PhoneNumberTypeEnum as v1PhoneNumberTypeEnum,
  PhoneNumberCountryCodeSourceEnum as v1PhoneNumberCountryCodeSourceEnum,
} from '../types/api/v1/common/phone_number';
import type {
  IAirSearchUrlPaxInfoWithUserOrgId,
  ILoyaltyInfo,
  IPaxSegmentSeatNumber,
  IPaxSelectedLoyalty,
} from '../types/flight';
import { FlightBookingType } from '../types/common';
import type { FarePrefFareTypesEnum } from '../types/api/v2/obt/model/fare-pref';
import type { AlliancePrefAlliancesEnum } from '../types/api/v2/obt/model/alliance-pref';
import type { AirlinePrefFlightTypeEnum } from '../types/api/v2/obt/model/airline-pref';
import type { Tier } from '../types/api/v2/obt/model/tier';
import type { Traveler as V2Traveler } from '../types/api/v2/obt/model/traveler';
import type { UserTitleEnum, UserStatusEnum, UserGenderEnum } from '../types/api/v2/obt/model/user';
import type { CardOwnershipLabelEnum, CardTypeEnum } from '../types/api/v2/obt/model/card';
import type { PaymentInfo, PaymentInfoApplicableToEnum } from '../types/api/v2/obt/model/payment-info';
import type { PhoneNumberCountryCodeSourceEnum, PhoneNumberTypeEnum } from '../types/api/v2/obt/model/phone-number';
import type { Persona } from '../types/api/v2/obt/model/persona';
import type {
  SeatLocationPrefCabinsEnum,
  SeatLocationPrefPositionEnum,
} from '../types/api/v2/obt/model/seat-location-pref';
import type { MealPrefExclMealPrefsEnum, MealPrefInclMealPrefsEnum } from '../types/api/v2/obt/model/meal-pref';
import type { SeatAmenityPrefSeatAmenityTypesEnum } from '../types/api/v2/obt/model/seat-amenity-pref';
import {
  FarePrefFareTypeEnum,
  SeatAmenityPrefAirSeatAmenityTypeEnum,
  SeatLocationPrefPositionEnum as v1SeatLocationPrefPositionEnum,
} from '../types/api/v1/obt/common/air_preference';

import { serializeJSON } from '../utils/common';
import type { User, PaymentInfo as v1PaymentInfo } from '../types/api/v1/obt/common/user';
import {
  PaymentInfoApplicableToEnum as v1PaymentInfoApplicableToEnum,
  UserStatusEnum as v1UserStatusEnum,
  PassportTypeEnum as v1PassportTypeEnum,
  ImmigrationDocumentTypeEnum as v1ImmigrationDocumentTypeEnum,
  UserTitleEnum as v1UserTitleEnum,
  NationalDocType,
} from '../types/api/v1/obt/common/user';
import type {
  UpdateTravelerRequestType,
  UpdateTravelerRequestTypeEnum,
} from '../types/api/v1/obt/profile/update_traveler_request';
import type { TravelerPersonalInfo } from '../types/api/v1/obt/common/traveler_personal_info';
import { LoyaltyInfoTypeEnum } from '../types/api/v1/obt/common/traveler_personal_info';
import type { LoyaltyInfoTypeEnum as V2LoyaltyInfoTypeEnum } from '../types/api/v2/obt/model/loyalty-info';
import type { Passport, PassportTypeEnum } from '../types/api/v2/obt/model/passport';
import type { ImmigrationDocumentTypeEnum } from '../types/api/v2/obt/model/immigration-document';
import type { IdentityDocument as v2IdentityDocument } from '../types/api/v2/obt/model/identity-document';
import type { ListTripSummariesResponseAirPnrSummaryLegInfo } from '../types/api/v1/obt/trip/list_trips';

import {
  airlinesMap,
  CARD_DEFAULT_EXPIRY_MONTH,
  CARD_DEFAULT_EXPIRY_YEAR,
  mapNameSuffixFromV1ToV2,
} from '../constants';
import type { NonProfileUserInfo, TierEnumNumerals } from '../types/api/v1/obt/common/user_org_id';
import { PersonaEnum, TierEnum } from '../types/api/v1/obt/common/user_org_id';
import {
  CreditCardAccessType as CreditCardAccessTypeEnum,
  CardCompany as CardCompanyV1,
  CardType,
  CreditCardAccessCardAccessEntityCentralCardAccessLevel,
  OwnershipLabel,
} from '../types/api/v1/common/card';
import type { RailCard } from '../types/api/v1/common/card';
import type { AirPnrTravelerInfo } from '../types/api/v1/obt/pnr/pnr';
import { dateUtil } from '../date-utils';
import { AllianceEnum, CabinEnum, MealTypeEnum } from '../types/api/v1/obt/air/air_common';

import { v1EnumToV2EnumBEProtos, v1EnumToV2EnumGenderProtos } from './common';
import type { CentralCardAccessLevel } from '../types/api/v2/obt/model/central-card-access-level';
import { NameSuffix, NameSuffix as v1NameSuffixTypeEnum } from '../types/api/v1/common/name';
import type { CardCompany } from '../types/api/v2/obt/model/card-company';
import { PreferredLocationLabel as PreferredLocationLabelV1, TravelClass } from '../types/api/v1/obt/common/common';
import type { RailTravelClass } from '../types/api/v2/obt/model/rail-travel-class';
import { RateTypeV1 } from '../types/api/v1/obt/hotel/hotel_common';
import type { HotelPrefAmenity } from '../types/api/v2/obt/model/hotel-pref-amenity';
import {
  HotelPrefHotelPrefAmenity,
  RoomPreferenceBedCount,
  RoomPreferenceMostImportantFact,
  RoomPreferencePillowType,
  RoomPreferenceRoomAmenityPref,
  RoomPreferenceRoomLocation,
  RoomPreferenceRoomType,
} from '../types/api/v1/obt/common/hotel_preference';
import type {
  RoomPreferenceBedCountTypeEnum,
  RoomPreferenceMostImportantFactEnum,
  RoomPreferencePillowTypeEnum,
  RoomPreferenceRoomAmenityPrefsEnum,
  RoomPreferenceRoomLocationEnum,
  RoomPreferenceRoomTypeEnum,
} from '../types/api/v2/obt/model/room-preference';
import type { PreferredLocationLabel } from '../types/api/v2/obt/model/preferred-location-label';
import { CarType as CarTypeV1, EngineType as EngineTypeV1 } from '../types/api/v1/obt/car/car_common';
import type { CarType } from '../types/api/v2/obt/model/car-type';
import type { EngineType } from '../types/api/v2/obt/model/engine-type';
import type { TransmissionSearchFilter } from '../types/api/v2/obt/model/transmission-search-filter';
import { CarPrefTransmissionType } from '../types/api/v1/obt/common/car_preference';
import type {
  CoachPref,
  ConditionalRate,
  DeckLevel,
  Name,
  NationalDocTypeEnum,
  SeatPrefDirection,
  SeatPrefLocation,
  SeatPrefType,
  TravelPreferences,
} from '../types';
import { AirlinePrefFlightTypeEnumV1, AirPnrTravelerInfoSeatInfoSeatStatus } from '../types';
import {
  CoachPref as CoachPrefV1,
  SeatPrefDeckLevel,
  SeatPrefSeatDirection,
  SeatPrefSeatLocation,
  SeatPrefSeatType,
} from '../types/api/v1/obt/common/rail_preference';

interface ITravelerCheckoutDetails {
  contactNumber: string;
  paymentCardId?: string;
}

/**  @deprecated for v2-rearchitecture * */
export const getTravelerCheckoutDetails = (
  travelerDetailManager: TravelerDetailsResponseManager,
  { paymentCardId, contactNumber }: ITravelerCheckoutDetails,
  selectedLoyalty: ILoyaltyInfo | null | undefined,
  customPaymentCard?: ITravelerPaymentInfo,
  guestTravelerDetailsManager?: TravelerDetailsResponseManager,
): Traveler[] => {
  const detailsManager = guestTravelerDetailsManager ?? travelerDetailManager;
  const traveler: Traveler = cloneDeep(detailsManager.GetTraveler());
  // Setting payment information
  if (traveler.user) {
    traveler.user.paymentInfos =
      customPaymentCard && !paymentCardId
        ? [customPaymentCard]
        : travelerDetailManager
            .GetPaymentInfo()
            .filter((paymentInfo) => !!paymentCardId && paymentInfo.card?.id === paymentCardId);

    if (contactNumber) {
      traveler.user.phoneNumbers = traveler.user.phoneNumbers.filter(
        (phoneNumber) => phoneNumber.rawInput === contactNumber.trim(),
      );
    }
    // Setting loyalty info
    if (traveler.travelerPersonalInfo) {
      const isLoyaltyBlocked = traveler.travelerPersonalInfo.blockLoyalty ?? false;
      traveler.travelerPersonalInfo.loyaltyInfos = isLoyaltyBlocked || !selectedLoyalty ? [] : [selectedLoyalty];

      if (traveler.travelerPersonalInfo.travelPref?.railCards && isLoyaltyBlocked) {
        traveler.travelerPersonalInfo.travelPref.railCards = [];
      }
    }

    return serializeJSON([traveler]);
  }
  return [];
};

export const trimName = ({ given, middle, family1, family2, suffix, preferred }: ITravelerName): ITravelerName => ({
  given: given.trim(),
  middle: middle.trim(),
  family1: family1.trim(),
  family2: family2.trim(),
  preferred: preferred.trim(),
  suffix,
});

export const trimPhoneNumber = (phoneNumber: IPhoneNumber): IPhoneNumber => ({
  ...phoneNumber,
  rawInput: phoneNumber.rawInput.trim(),
});

export const trimAddress = (address: IPostalAddress): IPostalAddress => ({
  ...address,
  description: address.description.trim(),
  locality: address.locality.trim(),
  postalCode: address.postalCode.trim(),
  administrativeArea: address.administrativeArea.trim(),
  addressLines: address.addressLines.map((addressLine) => addressLine?.trim()),
});

export const trimPassport = (passport: IPassport): IPassport => ({
  ...passport,
  docId: passport.docId.trim(),
});

/**
 * @deprecated for v2
 * Returns an array of update types needed by traveler/update API for Personal page
 */
export const getTravelerPersonalUpdateTypes = (
  oldUserData?: User,
  updatedUserData?: User,
  oldTravelerTier?: TierEnumNumerals,
  updatedTravelerTier?: TierEnumNumerals,
): UpdateTravelerRequestType[] => {
  const updateTypes: UpdateTravelerRequestType[] = [];

  if (!oldUserData || !updatedUserData) return updateTypes;

  const compareAndAdd = (
    property1: User[keyof User],
    property2: User[keyof User],
    type: UpdateTravelerRequestType,
  ): void => {
    if (!isEqual(property1, property2)) updateTypes.push(type);
  };

  compareAndAdd(oldUserData.name, updatedUserData.name, IUpdateTravelerRequestTypeEnum.NAME);
  compareAndAdd(oldUserData.title, updatedUserData.title, IUpdateTravelerRequestTypeEnum.TITLE);
  compareAndAdd(oldUserData.gender, updatedUserData.gender, IUpdateTravelerRequestTypeEnum.GENDER);
  compareAndAdd(
    oldUserData.preferredPronoun,
    updatedUserData.preferredPronoun,
    IUpdateTravelerRequestTypeEnum.PREFERRED_PRONOUN,
  );
  compareAndAdd(oldUserData.dob, updatedUserData.dob, IUpdateTravelerRequestTypeEnum.DOB);
  compareAndAdd(oldUserData.phoneNumbers, updatedUserData.phoneNumbers, IUpdateTravelerRequestTypeEnum.PHONE_NUMBERS);
  compareAndAdd(oldUserData.email, updatedUserData.email, IUpdateTravelerRequestTypeEnum.EMAIL);
  compareAndAdd(oldUserData.addresses, updatedUserData.addresses, IUpdateTravelerRequestTypeEnum.POSTAL_ADDRESSES);
  compareAndAdd(oldUserData.identityDocs, updatedUserData.identityDocs, IUpdateTravelerRequestTypeEnum.IDENTITY_DOCS);
  compareAndAdd(
    oldUserData.emergencyContactInfo,
    updatedUserData.emergencyContactInfo,
    IUpdateTravelerRequestTypeEnum.EMERGENCY_CONTACT_INFO,
  );
  compareAndAdd(
    oldUserData.emergencyContact,
    updatedUserData.emergencyContact,
    IUpdateTravelerRequestTypeEnum.EMERGENCY_CONTACT,
  );
  compareAndAdd(
    oldUserData.profilePicture,
    updatedUserData.profilePicture,
    IUpdateTravelerRequestTypeEnum.PROFILE_PICTURE,
  );
  compareAndAdd(
    oldUserData.preferredLanguage,
    updatedUserData.preferredLanguage,
    IUpdateTravelerRequestTypeEnum.PREFERRED_LANGUAGE,
  );
  if (oldTravelerTier !== updatedTravelerTier) {
    updateTypes.push(IUpdateTravelerRequestTypeEnum.TIER);
  }

  return updateTypes;
};

export const getTravelerUpdateTypes = (
  oldTraveler: ITraveler,
  updatedTraveler: ITraveler,
  travelerPersistenceDisabledList: UpdateTravelerRequestTypeEnum[],
): UpdateTravelerRequestType[] => {
  const updateTypes: UpdateTravelerRequestType[] = [];

  // TODO: Check if this is needed
  if (
    !oldTraveler.user ||
    !updatedTraveler.user ||
    !updatedTraveler.travelerPersonalInfo ||
    !oldTraveler.travelerPersonalInfo
  ) {
    return updateTypes;
  }

  const compareAndAdd = (
    property1: User[keyof User] | TravelerPersonalInfo[keyof TravelerPersonalInfo],
    property2: User[keyof User] | TravelerPersonalInfo[keyof TravelerPersonalInfo],
    type: UpdateTravelerRequestType,
  ): void => {
    if (!travelerPersistenceDisabledList.includes(type)) {
      if (!isEqual(property1, property2)) updateTypes.push(type);
    }
  };

  compareAndAdd(oldTraveler.user.name, updatedTraveler.user.name, IUpdateTravelerRequestTypeEnum.NAME);
  compareAndAdd(oldTraveler.user.title, updatedTraveler.user.title, IUpdateTravelerRequestTypeEnum.TITLE);
  compareAndAdd(
    oldTraveler.user.preferredPronoun,
    updatedTraveler.user.preferredPronoun,
    IUpdateTravelerRequestTypeEnum.PREFERRED_PRONOUN,
  );

  compareAndAdd(oldTraveler.user.dob, updatedTraveler.user.dob, IUpdateTravelerRequestTypeEnum.DOB);

  compareAndAdd(oldTraveler.user.gender, updatedTraveler.user.gender, IUpdateTravelerRequestTypeEnum.GENDER);

  compareAndAdd(
    oldTraveler.user.phoneNumbers,
    updatedTraveler.user.phoneNumbers,
    IUpdateTravelerRequestTypeEnum.PHONE_NUMBERS,
  );
  compareAndAdd(oldTraveler.user.email, updatedTraveler.user.email, IUpdateTravelerRequestTypeEnum.EMAIL);
  compareAndAdd(
    oldTraveler.user.addresses,
    updatedTraveler.user.addresses,
    IUpdateTravelerRequestTypeEnum.POSTAL_ADDRESSES,
  );
  // Sorting Identity Docs to remove dependency on order for comparison
  compareAndAdd(
    oldTraveler.user.identityDocs.slice().sort((a, b) => (JSON.stringify(a) > JSON.stringify(b) ? 1 : -1)),
    updatedTraveler.user.identityDocs.slice().sort((a, b) => (JSON.stringify(a) > JSON.stringify(b) ? 1 : -1)),
    IUpdateTravelerRequestTypeEnum.IDENTITY_DOCS,
  );
  compareAndAdd(
    oldTraveler.user.emergencyContactInfo,
    updatedTraveler.user.emergencyContactInfo,
    IUpdateTravelerRequestTypeEnum.EMERGENCY_CONTACT_INFO,
  );

  compareAndAdd(
    oldTraveler.user.emergencyContact,
    updatedTraveler.user.emergencyContact,
    IUpdateTravelerRequestTypeEnum.EMERGENCY_CONTACT,
  );

  compareAndAdd(
    oldTraveler.travelerPersonalInfo.loyaltyInfos,
    updatedTraveler.travelerPersonalInfo.loyaltyInfos,
    IUpdateTravelerRequestTypeEnum.LOYALTY_INFOS,
  );

  compareAndAdd(
    oldTraveler.travelerPersonalInfo.travelPref,
    updatedTraveler.travelerPersonalInfo.travelPref,
    IUpdateTravelerRequestTypeEnum.TRAVEL_PREF,
  );

  compareAndAdd(
    oldTraveler.user.profilePicture,
    updatedTraveler.user.profilePicture,
    IUpdateTravelerRequestTypeEnum.PROFILE_PICTURE,
  );

  compareAndAdd(
    oldTraveler.userBusinessInfo,
    updatedTraveler.userBusinessInfo,
    IUpdateTravelerRequestTypeEnum.EMAIL_CONFIGURATION,
  );

  compareAndAdd(
    oldTraveler.user?.relativeInfo,
    updatedTraveler.user?.relativeInfo,
    IUpdateTravelerRequestTypeEnum.RELATION,
  );

  compareAndAdd(oldTraveler.tier, updatedTraveler.tier, IUpdateTravelerRequestTypeEnum.TIER);

  compareAndAdd(
    oldTraveler.user.nationality,
    updatedTraveler.user.nationality,
    IUpdateTravelerRequestTypeEnum.NATIONALITY,
  );

  compareAndAdd(
    oldTraveler.user.preferredLanguage,
    updatedTraveler.user.preferredLanguage,
    IUpdateTravelerRequestTypeEnum.PREFERRED_LANGUAGE,
  );

  compareAndAdd(
    oldTraveler.user.travelerName,
    updatedTraveler.user.travelerName,
    IUpdateTravelerRequestTypeEnum.TRAVELER_NAME,
  );

  return updateTypes;
};

export const getTravelerUserUpdate = (
  traveler: ITraveler,
  {
    contactInfo: { email, phoneNumbers },
    name,
    bio: { gender, preferredPronoun, dob },
    address,
    title,
    travelIDs: { passports, nationalDoc, knownTravelerNumber, redressNumber, ktn, redress },
    emergencyContact,
    emergencyContactInfo,
    profilePicture,
    tier,
    preferredLanguage,
  }: ITravelerPersonalInfo,
): ITraveler => {
  const passportList = passports.map((passport) => ({ passport }));
  const nationalDocList = nationalDoc.map((doc) => ({ nationalDoc: doc }));
  const user: User | undefined = traveler.user && {
    ...traveler.user,
    email: email.trim(),
    name: trimName(name),
    gender,
    title,
    preferredPronoun,
    dob: dob ? { iso8601: dob } : undefined,
    addresses: address,
    phoneNumbers,
    identityDocs: [...passportList, ...nationalDocList],
    preferredLanguage: preferredLanguage || '',
    // Since the name is a required field in the emergency contact, if it exists, we can assume the other data will be present as well.
    emergencyContact,
    // checking if given name exists in emergency contact, if yes then other data would also be there, otherwise send
    // {email : '', designation : ''} which is sent by backend for new users
    emergencyContactInfo: emergencyContactInfo?.name?.given
      ? {
          ...emergencyContactInfo,
          phoneNumber: emergencyContactInfo.phoneNumber.rawInput ? emergencyContactInfo.phoneNumber : undefined,
        }
      : { email: '', designation: '' },
    profilePicture: profilePicture || (traveler.user.profilePicture ? { data: '', url: '' } : undefined),
  };

  if (user && ktn?.issueCountry.trim() && ktn?.number.trim()) {
    user.identityDocs.push({ ktn: { number: ktn?.number.trim(), issueCountry: ktn?.issueCountry.trim() } });
    user.identityDocs.push({ knownTravelerNumber: ktn?.number.trim() });
  } else if (user && knownTravelerNumber?.trim()) {
    user.identityDocs.push({ knownTravelerNumber: knownTravelerNumber.trim() });
  }

  if (user && redress?.issueCountry.trim() && redress?.number.trim()) {
    user.identityDocs.push({ redress: { number: redress?.number.trim(), issueCountry: redress?.issueCountry.trim() } });
    user.identityDocs.push({ redressNumber: redress?.number.trim() });
  } else if (user && redressNumber?.trim()) {
    user.identityDocs.push({ redressNumber: redressNumber.trim() });
  }

  const updatedTraveler = {
    ...traveler,
    user,
    tier,
  };
  return updatedTraveler;
};

export const getRailcardFromLoyaltyInfo = (loyaltyInfo: ITravelerLoyaltyInfoV2): RailCard => {
  return {
    name: first(loyaltyInfo.appliedTo) ?? '',
    spotnanaCode: loyaltyInfo.id,
    vendor: loyaltyInfo.issuedBy,
    cardNumber: loyaltyInfo.cardNumber ?? '',
    expiryDate: { iso8601: loyaltyInfo.expiryDate ?? '' },
  };
};

export const getTravelerLoyaltyUpdate = (traveler: ITraveler, loyaltyData: ITravelerLoyaltyInfos): ITraveler => {
  const loyaltyInfos = [...loyaltyData.air, ...loyaltyData.hotel, ...loyaltyData.car];

  const railCards = loyaltyData.rail.map((railLoyaltyInfo) => getRailcardFromLoyaltyInfo(railLoyaltyInfo));
  return {
    ...traveler,
    travelerPersonalInfo: {
      ...traveler.travelerPersonalInfo,
      travelPref: {
        ...traveler.travelerPersonalInfo?.travelPref,
        railCards,
      },
      loyaltyInfos,
      blockLoyalty: traveler.travelerPersonalInfo?.blockLoyalty ?? false,
    },
  };
};

export const getTravelerAirPrefUpdate = (traveler: ITraveler, travelAirPref: IAirPref): ITraveler => {
  const travelPref = { ...(traveler?.travelerPersonalInfo?.travelPref || { railCards: [] }), airPref: travelAirPref };

  return produce(traveler, (draft) => {
    if (travelPref && draft.travelerPersonalInfo) {
      draft.travelerPersonalInfo.travelPref = travelPref;
    } else {
      draft.travelerPersonalInfo = {
        travelPref: { airPref: travelAirPref, railCards: [] },
        loyaltyInfos: [],
        blockLoyalty: traveler.travelerPersonalInfo?.blockLoyalty ?? false,
      };
    }
  });
};

export const getTravelerAlertPrefUpdate = (traveler: ITraveler, alertPreferences: IEmailConfiguration): ITraveler => {
  if (!traveler.userBusinessInfo) {
    return traveler;
  }
  const emailConfiguration = { ...alertPreferences };
  return {
    ...traveler,
    userBusinessInfo: {
      ...traveler.userBusinessInfo,
      emailConfiguration,
    },
  };
};

export const getUserOrgIdFromTravelerData = (traveler: ITraveler | null): IUserOrgId => {
  const userOrgId: IUserOrgId = {
    userId: { id: '' },
    organizationId: { id: '' },
  };
  if (traveler) {
    const travelerDetailsResponseManager = new TravelerDetailsResponseManager(traveler);
    const userId = travelerDetailsResponseManager.GetUserBusinessId();
    const orgId = travelerDetailsResponseManager.GetOrganizationId();
    userOrgId.userId = { id: userId };
    userOrgId.organizationId = { id: orgId };
  }
  return userOrgId;
};

export const getAirportsFromFlightPnrSummary = (flightSummary: IFlightPnrSummary): string[] =>
  flightSummary.flights.reduce((airports: string[], flight) => {
    if (last(airports) !== flight.origin) {
      airports.push(flight.origin);
    }
    airports.push(flight.destination);
    return airports;
  }, []);

export function getFlightNamesForLeg(legInfo: ListTripSummariesResponseAirPnrSummaryLegInfo): string {
  return legInfo.flights
    .map((flightInfo) => flightInfo.marketing?.airline)
    .filter((airlineCode) => !!airlineCode)
    .map((airlineCode) => airlinesMap[airlineCode as string] || airlineCode)
    .join(', ');
}

export function getFlightBookingType(allLegs: ListTripSummariesResponseAirPnrSummaryLegInfo[]): FlightBookingType {
  // More than 2 legs is multi city.
  if (allLegs.length > 2) {
    return FlightBookingType.MULTI_CITY;
  }

  // For 1 or lesser, return one way. Less than is for edge case in case backend
  // send zero legs.
  if (allLegs.length <= 1) {
    return FlightBookingType.ONE_WAY;
  }

  // Case for exactly 2 legs.
  const firstLegFlights = allLegs[0].flights;
  const numFlightsInFirstLeg = firstLegFlights.length;
  const firstLegOrigin = firstLegFlights[0].origin;
  const firstLegDestination = firstLegFlights[numFlightsInFirstLeg - 1].destination;

  const secondLegFlights = allLegs[1].flights;
  const numFlightsInSecondLeg = secondLegFlights.length;
  const secondLegOrigin = secondLegFlights[0].origin;
  const secondLegDestination = secondLegFlights[numFlightsInSecondLeg - 1].destination;

  if (firstLegOrigin === secondLegDestination && secondLegOrigin === firstLegDestination) {
    return FlightBookingType.ROUND_TRIP;
  }

  return FlightBookingType.MULTI_CITY;
}

//  baggage info is not returned
export const getPaxInfoFromTravelerInfo = (
  travelerInfo: AirPnrTravelerInfo,
  dataFlightIndex?: number,
  loyalty?: IPaxSelectedLoyalty[],
): IAirSearchUrlPaxInfoWithUserOrgId => {
  if (!travelerInfo.traveler?.userOrgId) {
    throw new Error('userOrgId is required in PNR');
  }

  const user = travelerInfo.traveler?.user;
  const paxAgeInYears = user?.dob?.iso8601 ? dateUtil(dateUtil()).diff(user.dob.iso8601, 'years') : undefined;

  let seat: IPaxSegmentSeatNumber[] = [];

  if (dataFlightIndex !== undefined) {
    seat = travelerInfo.seatInfo
      .filter(
        (seatInfo) =>
          seatInfo.flightIndex === dataFlightIndex &&
          seatInfo.status !== AirPnrTravelerInfoSeatInfoSeatStatus.CANCELLED,
      )
      .map((seatInfo) => ({ flightIndex: dataFlightIndex, seatNumber: seatInfo.number }));
  }
  return {
    paxType: travelerInfo.paxType ?? 0,
    passport: travelerInfo.traveler?.user?.identityDocs?.find((doc) => doc.passport)?.passport,
    paxAge: paxAgeInYears ? { years: paxAgeInYears } : undefined,
    userId: travelerInfo.traveler?.userOrgId?.userId?.id,
    userOrgId: travelerInfo.traveler?.userOrgId,
    seat,
    gender: user?.gender || null,
    phone: first(user?.phoneNumbers)?.rawInput || null,
    preBookAnswers: null,
    dob: travelerInfo.traveler.user?.dob?.iso8601 || null,
    loyalty: loyalty || [],
    isLoyaltyInitialized: true,
    doNotShareEmailAndPhone: false,
  };
};

// TODO (abhi): Convert the adaptor to immer
export const v1ToV2TravelerAdaptor = (v1Traveler: ITraveler, v2TravelPref?: TravelPreferences): V2Traveler => {
  const travelPref = v1Traveler.travelerPersonalInfo?.travelPref;
  const airPref = travelPref?.airPref;
  const v1User = v1Traveler.user;
  const emergencyContactInfo = v1User?.emergencyContactInfo;
  const emergencyContact = v1User?.emergencyContact;

  const identityDocsv1 =
    v1Traveler.user?.identityDocs.filter(
      (travelDoc) =>
        travelDoc?.redressNumber ||
        travelDoc?.knownTravelerNumber ||
        travelDoc?.ktn ||
        travelDoc?.redress ||
        travelDoc.passport?.docId ||
        travelDoc.nationalDoc?.docId,
    ) || [];

  const identityDocsV2: v2IdentityDocument[] = identityDocsv1
    ?.map((doc) => {
      if (doc.passport !== undefined) {
        let nameV2: Name | undefined;
        if (doc.passport.name) {
          nameV2 = {
            ...doc.passport.name,
            suffix: mapNameSuffixFromV1ToV2[doc.passport.name.suffix ?? NameSuffix.NAME_SUFFIX_UNKNOWN],
          };
        }

        const passportV2: Passport = {
          ...doc.passport,
          name: nameV2,
          type: v1EnumToV2EnumBEProtos<PassportTypeEnum>(doc.passport.type, v1PassportTypeEnum),
        };

        const passportIdentityDoc: v2IdentityDocument = {
          passport: passportV2,
        };

        return passportIdentityDoc;
      }

      if (doc.immigrationDoc !== undefined) {
        const immigrationDocIdentityDoc: v2IdentityDocument = {
          immigrationDoc: {
            ...doc.immigrationDoc,
            type: v1EnumToV2EnumBEProtos<ImmigrationDocumentTypeEnum>(
              doc.immigrationDoc.type,
              v1ImmigrationDocumentTypeEnum,
            ),
          },
        };

        return immigrationDocIdentityDoc;
      }

      if (doc.nationalDoc !== undefined) {
        const nationalDocIdentityDoc: v2IdentityDocument = {
          nationalDoc: {
            ...doc.nationalDoc,
            type:
              doc.nationalDoc.type === NationalDocType.TYPE_UNKNOWN ||
              doc.nationalDoc.type === NationalDocType.UNRECOGNIZED
                ? undefined
                : v1EnumToV2EnumBEProtos<NationalDocTypeEnum>(doc.nationalDoc.type, NationalDocType),
          },
        };

        return nationalDocIdentityDoc;
      }

      if (doc.ktn !== undefined) {
        const ktnIdentityDoc: v2IdentityDocument = {
          ktn: doc.ktn,
        };
        return ktnIdentityDoc;
      }

      if (doc.redress !== undefined) {
        const redressIdentityDoc: v2IdentityDocument = {
          redress: doc.redress,
        };
        return redressIdentityDoc;
      }

      return doc;
    })
    .filter((doc): doc is v2IdentityDocument => !!doc);

  return {
    userOrgId: v1Traveler.userOrgId,
    // send adhocUserInfo key only if adhocUserInfo profileOwner.userId.id is present
    ...(v1Traveler.adhocUserInfo?.profileOwner?.userId?.id && {
      adhocUserInfo: {
        profileOwner: {
          userId: {
            id: v1Traveler.adhocUserInfo?.profileOwner?.userId?.id ?? '',
          },
        },
        isSaved: v1Traveler.adhocUserInfo.isSaved,
      },
    }),
    userBusinessInfo: {
      ...v1Traveler.userBusinessInfo,
      email: v1Traveler.userBusinessInfo?.email ?? '',
      employeeId: v1Traveler.userBusinessInfo?.employeeId ?? '',
      legalEntityId: v1Traveler.userBusinessInfo?.legalEntityId ?? { id: '' },
      phoneNumbers:
        v1Traveler.userBusinessInfo?.phoneNumbers.map((phoneNumber) => ({
          ...phoneNumber,
          type: v1EnumToV2EnumBEProtos<PhoneNumberTypeEnum>(phoneNumber?.type, v1PhoneNumberTypeEnum),
          countryCodeSource: v1EnumToV2EnumBEProtos<PhoneNumberCountryCodeSourceEnum>(
            phoneNumber?.countryCodeSource,
            v1PhoneNumberCountryCodeSourceEnum,
          ),
        })) ?? [],
      managerBasicInfo: {
        ...v1Traveler.userBusinessInfo?.managerBasicInfo,
        persona: v1EnumToV2EnumBEProtos<Persona>(v1Traveler.userBusinessInfo?.managerBasicInfo?.persona, PersonaEnum),
        profilePicture: {
          ...v1Traveler.userBusinessInfo?.managerBasicInfo?.profilePicture,
          data: v1Traveler.userBusinessInfo?.managerBasicInfo?.profilePicture?.data as string,
        },
        tier: v1EnumToV2EnumBEProtos<Tier>(v1Traveler.userBusinessInfo?.managerBasicInfo?.tier, TierEnum),

        name: v1Traveler.userBusinessInfo?.managerBasicInfo?.name
          ? {
              ...v1Traveler.userBusinessInfo.managerBasicInfo.name,
              suffix:
                mapNameSuffixFromV1ToV2[
                  v1Traveler.userBusinessInfo?.managerBasicInfo?.name?.suffix ??
                    v1NameSuffixTypeEnum.NAME_SUFFIX_UNKNOWN
                ],
            }
          : undefined,
      },
      organizationId: v1Traveler?.userOrgId?.organizationId ?? { id: '' },
    },
    user: {
      ...v1User,
      email: v1User?.email ?? '',
      profilePicture: { ...v1User?.profilePicture, data: v1User?.profilePicture?.data as string },
      status: v1EnumToV2EnumBEProtos<UserStatusEnum>(v1User?.status, v1UserStatusEnum),
      title: v1EnumToV2EnumBEProtos<UserTitleEnum>(v1User?.title, v1UserTitleEnum),
      name: {
        ...v1User?.name,
        suffix: mapNameSuffixFromV1ToV2[v1User?.name?.suffix ?? v1NameSuffixTypeEnum.NAME_SUFFIX_UNKNOWN],
      },
      emergencyContact: TravelerDetailsResponseManager.convertEmergencyContactToV2(emergencyContact),
      emergencyContactInfo: {
        ...emergencyContactInfo,
        email: emergencyContactInfo?.email ?? '',
        phoneNumber: {
          ...emergencyContactInfo?.phoneNumber,
          type: v1EnumToV2EnumBEProtos<PhoneNumberTypeEnum>(
            emergencyContactInfo?.phoneNumber?.type,
            v1PhoneNumberTypeEnum,
          ),
          countryCodeSource: v1EnumToV2EnumBEProtos<PhoneNumberCountryCodeSourceEnum>(
            emergencyContactInfo?.phoneNumber?.countryCodeSource,
            v1PhoneNumberCountryCodeSourceEnum,
          ),
        },
        name: emergencyContactInfo?.name
          ? {
              ...emergencyContactInfo.name,
              suffix:
                mapNameSuffixFromV1ToV2[emergencyContactInfo?.name?.suffix ?? v1NameSuffixTypeEnum.NAME_SUFFIX_UNKNOWN],
            }
          : undefined,
      },
      gender: v1EnumToV2EnumGenderProtos<UserGenderEnum>(v1User?.gender),
      identityDocs: identityDocsV2,
      paymentInfos:
        v1User?.paymentInfos?.map((paymentInfo) => ({
          ...paymentInfo,
          applicableTo: paymentInfo.applicableTo?.map((applicableTo) =>
            v1EnumToV2EnumBEProtos<PaymentInfoApplicableToEnum>(applicableTo, v1PaymentInfoApplicableToEnum),
          ),
          card: {
            ...paymentInfo.card,
            ownershipLabel:
              paymentInfo.card?.ownershipLabel === OwnershipLabel.OWNERSHIP_LABEL_UNKNOWN
                ? undefined
                : v1EnumToV2EnumBEProtos<CardOwnershipLabelEnum>(paymentInfo.card?.ownershipLabel, OwnershipLabel),
            type: v1EnumToV2EnumBEProtos<CardTypeEnum>(paymentInfo.card?.type, CardType),
            company: v1EnumToV2EnumBEProtos<CardCompany>(paymentInfo.card?.company, CardCompanyV1),
            id: paymentInfo.card?.id ?? '',
            name: paymentInfo.card?.name ?? '',
            number: paymentInfo.card?.number ?? '',
            expiryMonth: paymentInfo.card?.expiryMonth ?? CARD_DEFAULT_EXPIRY_MONTH,
            expiryYear: paymentInfo.card?.expiryYear ?? CARD_DEFAULT_EXPIRY_YEAR,
          },
          accessType: v1EnumToV2EnumBEProtos<CreditCardAccessType>(paymentInfo.accessType, CreditCardAccessTypeEnum),
          access: {
            ...paymentInfo.access,
            accessType: v1EnumToV2EnumBEProtos<CreditCardAccessType>(
              paymentInfo.access?.accessType,
              CreditCardAccessTypeEnum,
            ),
            entityIds: paymentInfo.access?.entityIds ?? [],
            entities:
              paymentInfo.access?.entities?.map((entity) => ({
                ...entity,
                centralCardAccessLevel: v1EnumToV2EnumBEProtos<CentralCardAccessLevel>(
                  entity.centralCardAccessLevel,
                  CreditCardAccessCardAccessEntityCentralCardAccessLevel,
                ),
              })) ?? [],
          },
        })) ?? [],
      phoneNumbers:
        v1User?.phoneNumbers?.map((phoneNumber) => ({
          ...phoneNumber,
          type: v1EnumToV2EnumBEProtos<PhoneNumberTypeEnum>(phoneNumber?.type, v1PhoneNumberTypeEnum),
          countryCodeSource: v1EnumToV2EnumBEProtos<PhoneNumberCountryCodeSourceEnum>(
            phoneNumber?.countryCodeSource,
            v1PhoneNumberCountryCodeSourceEnum,
          ),
        })) ?? [],
    },
    travelerPersonalInfo: {
      loyaltyInfos:
        v1Traveler.travelerPersonalInfo?.loyaltyInfos?.map((loyalty) => ({
          ...loyalty,
          type: v1EnumToV2EnumBEProtos<V2LoyaltyInfoTypeEnum>(loyalty.type, LoyaltyInfoTypeEnum),
        })) ?? [],
      travelPref: v2TravelPref
        ? { ...v2TravelPref.preferences }
        : {
            ...travelPref,
            airPref: {
              preferredAirports: airPref?.preferredAirports?.map((preferredAirport) => ({
                ...preferredAirport,
                label: v1EnumToV2EnumBEProtos<PreferredLocationLabel>(preferredAirport.label, PreferredLocationLabelV1),
              })),
              numStopPref: airPref?.numStopPref,
              airlinePrefs:
                airPref?.airlinePrefs?.map((airlinePref) => ({
                  airlines: airlinePref.airlines,
                  flightType: v1EnumToV2EnumBEProtos<AirlinePrefFlightTypeEnum>(
                    airlinePref.flightType,
                    AirlinePrefFlightTypeEnumV1,
                  ),
                })) ?? [],
              farePref: {
                fareTypes:
                  airPref?.farePref?.fareTypes?.map((fareType) =>
                    v1EnumToV2EnumBEProtos<FarePrefFareTypesEnum>(fareType, FarePrefFareTypeEnum),
                  ) ?? [],
              },
              homeAirport: airPref?.homeAirport ?? '',
              alliancePref: {
                ...airPref?.alliancePref,
                alliances:
                  airPref?.alliancePref?.alliances?.map((alliance) =>
                    v1EnumToV2EnumBEProtos<AlliancePrefAlliancesEnum>(alliance, AllianceEnum),
                  ) ?? [],
              },
              mealPref: {
                inclMealPrefs: airPref?.mealPref?.inclMealPrefs?.map((mealPref) =>
                  v1EnumToV2EnumBEProtos<MealPrefInclMealPrefsEnum>(mealPref, MealTypeEnum),
                ),
                exclMealPrefs: airPref?.mealPref?.exclMealPrefs?.map((mealPref) =>
                  v1EnumToV2EnumBEProtos<MealPrefExclMealPrefsEnum>(mealPref, MealTypeEnum),
                ),
              },
              seatAmenityPref: {
                seatAmenityTypes:
                  airPref?.seatAmenityPref?.seatAmenityTypes?.map((seatAmenityType) =>
                    v1EnumToV2EnumBEProtos<SeatAmenityPrefSeatAmenityTypesEnum>(
                      seatAmenityType,
                      SeatAmenityPrefAirSeatAmenityTypeEnum,
                    ),
                  ) ?? [],
              },
              seatLocationPrefs:
                airPref?.seatLocationPrefs?.map((seatLocationPref) => ({
                  ...seatLocationPref,
                  cabin: undefined,
                  cabins: seatLocationPref.cabins.map((cabin) =>
                    v1EnumToV2EnumBEProtos<SeatLocationPrefCabinsEnum>(cabin, CabinEnum),
                  ),
                  position: v1EnumToV2EnumBEProtos<SeatLocationPrefPositionEnum>(
                    seatLocationPref.position,
                    v1SeatLocationPrefPositionEnum,
                  ),
                })) ?? [],
            },
            railCards: travelPref?.railCards ?? [],
            railPref: {
              preferredRailStations: travelPref?.railPref?.preferredRailStations?.map((preferredRailStation) => ({
                ...preferredRailStation,
                label: v1EnumToV2EnumBEProtos<PreferredLocationLabel>(
                  preferredRailStation.label,
                  PreferredLocationLabelV1,
                ),
              })),
              seatPreference: {
                hasAccessibility: travelPref?.railPref?.seatPref?.hasAccessibility,
                seatTypes: travelPref?.railPref?.seatPref?.seatTypes?.map((seatType) =>
                  v1EnumToV2EnumBEProtos<SeatPrefType>(seatType, SeatPrefSeatType),
                ),
                seatLocations: travelPref?.railPref?.seatPref?.seatLocations?.map((seatLocation) =>
                  v1EnumToV2EnumBEProtos<SeatPrefLocation>(seatLocation, SeatPrefSeatLocation),
                ),
                deckLevels: travelPref?.railPref?.seatPref?.deckLevels?.map((deckLevel) =>
                  v1EnumToV2EnumBEProtos<DeckLevel>(deckLevel, SeatPrefDeckLevel),
                ),
                seatDirections: travelPref?.railPref?.seatPref?.seatDirections?.map((seatDirection) =>
                  v1EnumToV2EnumBEProtos<SeatPrefDirection>(seatDirection, SeatPrefSeatDirection),
                ),
              },
              travelClasses: travelPref?.railPref?.travelClasses?.map((travelClass) =>
                v1EnumToV2EnumBEProtos<RailTravelClass>(travelClass, TravelClass),
              ),
              coachPreferences: travelPref?.railPref?.coachPrefs?.map((coachPref) =>
                v1EnumToV2EnumBEProtos<CoachPref>(coachPref, CoachPrefV1),
              ),
              conditionalRates: travelPref?.railPref?.rateTypes?.map((rateType) =>
                v1EnumToV2EnumBEProtos<ConditionalRate>(rateType, RateTypeV1),
              ),
            },
            hotelPref: {
              hotelParentChains: travelPref?.hotelPref?.hotelParentChains,
              hotelBrands: travelPref?.hotelPref?.hotelBrands,
              hotelAmenityTypes: travelPref?.hotelPref?.hotelPrefAmenities?.map((hotelPrefAmenity) =>
                v1EnumToV2EnumBEProtos<HotelPrefAmenity>(hotelPrefAmenity, HotelPrefHotelPrefAmenity),
              ),
              ...((travelPref?.hotelPref?.roomPreference?.isMobilityAccessible !== undefined ||
                travelPref?.hotelPref?.roomPreference?.bedCount ||
                travelPref?.hotelPref?.roomPreference?.roomType ||
                travelPref?.hotelPref?.roomPreference?.mostImportantFact ||
                travelPref?.hotelPref?.roomPreference?.roomLocation ||
                travelPref?.hotelPref?.roomPreference?.pillowType ||
                (travelPref?.hotelPref?.roomPreference?.roomAmenityPrefs &&
                  travelPref?.hotelPref?.roomPreference?.roomAmenityPrefs.length > 0)) && {
                roomPreference: {
                  // multi select key will be empty array if preference is not set, single select key should not be sent
                  ...(travelPref?.hotelPref?.roomPreference?.isMobilityAccessible !== undefined && {
                    isMobilityAccessible: travelPref?.hotelPref?.roomPreference?.isMobilityAccessible,
                  }),
                  ...(travelPref?.hotelPref?.roomPreference?.bedCount && {
                    bedCount: v1EnumToV2EnumBEProtos<RoomPreferenceBedCountTypeEnum>(
                      travelPref?.hotelPref?.roomPreference.bedCount,
                      RoomPreferenceBedCount,
                    ),
                  }),
                  ...(travelPref?.hotelPref?.roomPreference?.roomType && {
                    roomType: v1EnumToV2EnumBEProtos<RoomPreferenceRoomTypeEnum>(
                      travelPref?.hotelPref?.roomPreference?.roomType,
                      RoomPreferenceRoomType,
                    ),
                  }),
                  roomAmenityPrefs: travelPref?.hotelPref?.roomPreference?.roomAmenityPrefs?.map((roomAmenityPref) =>
                    v1EnumToV2EnumBEProtos<RoomPreferenceRoomAmenityPrefsEnum>(
                      roomAmenityPref,
                      RoomPreferenceRoomAmenityPref,
                    ),
                  ),
                  ...(travelPref?.hotelPref?.roomPreference?.mostImportantFact && {
                    mostImportantFact: v1EnumToV2EnumBEProtos<RoomPreferenceMostImportantFactEnum>(
                      travelPref?.hotelPref?.roomPreference?.mostImportantFact,
                      RoomPreferenceMostImportantFact,
                    ),
                  }),
                  ...(travelPref?.hotelPref?.roomPreference?.roomLocation && {
                    roomLocation: v1EnumToV2EnumBEProtos<RoomPreferenceRoomLocationEnum>(
                      travelPref?.hotelPref?.roomPreference?.roomLocation,
                      RoomPreferenceRoomLocation,
                    ),
                  }),
                  ...(travelPref?.hotelPref?.roomPreference?.pillowType && {
                    pillowType: v1EnumToV2EnumBEProtos<RoomPreferencePillowTypeEnum>(
                      travelPref?.hotelPref?.roomPreference?.pillowType,
                      RoomPreferencePillowType,
                    ),
                  }),
                },
              }),
              conditionalRates: travelPref?.hotelPref?.rateTypes?.map((rateType) =>
                v1EnumToV2EnumBEProtos<ConditionalRate>(rateType, RateTypeV1),
              ),
            },
            carPref: {
              vendors: travelPref?.carPref?.vendors?.map((vendor) => ({ code: vendor.code, name: vendor.name })),
              carTypes: travelPref?.carPref?.carTypes?.map((carType) =>
                v1EnumToV2EnumBEProtos<CarType>(carType, CarTypeV1),
              ),
              engineTypes: travelPref?.carPref?.engineTypes?.map((engineType) =>
                v1EnumToV2EnumBEProtos<EngineType>(engineType, EngineTypeV1),
              ),
              transmissionTypes: travelPref?.carPref?.transmissionTypes?.map((transmissionType) =>
                v1EnumToV2EnumBEProtos<TransmissionSearchFilter>(transmissionType, CarPrefTransmissionType),
              ),
              conditionalRates: travelPref?.carPref?.rateTypes?.map((rateType) =>
                v1EnumToV2EnumBEProtos<ConditionalRate>(rateType, RateTypeV1),
              ),
            },
          },
    },
    persona: v1EnumToV2EnumBEProtos<Persona>(v1Traveler.persona, PersonaEnum),
    tier: v1EnumToV2EnumBEProtos<Tier>(v1Traveler.tier, TierEnum),
    isActive: v1Traveler.isActive,
  };
};

export const getNonProfileUserInfos = (traveler: ITraveler | undefined): NonProfileUserInfo => {
  const nonProfileUserInfo: NonProfileUserInfo = {
    organizationId: traveler?.userOrgId?.organizationId,
    legalEntityId: traveler?.userBusinessInfo?.legalEntityId,
    registrarUserId: traveler?.userOrgId.userId,
  };
  return nonProfileUserInfo;
};

export const v1ToV2PaymentCardAdaptor = (paymentCard: v1PaymentInfo): PaymentInfo => {
  return {
    ...paymentCard,
    applicableTo: paymentCard.applicableTo?.map((applicableTo) =>
      v1EnumToV2EnumBEProtos<PaymentInfoApplicableToEnum>(applicableTo, v1PaymentInfoApplicableToEnum),
    ),
    card: {
      ...paymentCard.card,
      type: v1EnumToV2EnumBEProtos<CardTypeEnum>(paymentCard.card?.type, CardType),
      company: v1EnumToV2EnumBEProtos<CardCompany>(paymentCard.card?.company, CardCompanyV1),
      id: paymentCard.card?.id ?? '',
      name: paymentCard.card?.name ?? '',
      number: paymentCard.card?.number ?? '',
      expiryMonth: paymentCard.card?.expiryMonth,
      expiryYear: paymentCard.card?.expiryYear,
      ownershipLabel:
        paymentCard.card?.ownershipLabel === OwnershipLabel.OWNERSHIP_LABEL_UNKNOWN
          ? undefined
          : v1EnumToV2EnumBEProtos<CardOwnershipLabelEnum>(paymentCard.card?.ownershipLabel, OwnershipLabel),
    },
    accessType: v1EnumToV2EnumBEProtos<CreditCardAccessType>(paymentCard.accessType, CreditCardAccessTypeEnum),
    access: {
      ...paymentCard.access,
      accessType: v1EnumToV2EnumBEProtos<CreditCardAccessType>(
        paymentCard.access?.accessType,
        CreditCardAccessTypeEnum,
      ),
      entityIds: paymentCard.access?.entityIds ?? [],
      entities:
        paymentCard.access?.entities?.map((entity) => ({
          ...entity,
          centralCardAccessLevel: v1EnumToV2EnumBEProtos<CentralCardAccessLevel>(
            entity.centralCardAccessLevel,
            CreditCardAccessCardAccessEntityCentralCardAccessLevel,
          ),
        })) ?? [],
    },
  };
};
